import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { safeRedirectPath } from '@architeia/auth'
import { useAuthStore } from '@/stores/auth'
import { useAccountReturnStore } from '@/stores/accountReturn'
import { useAgeGateStore } from '@/stores/ageGate'
import { applyDocumentTitle } from '@/i18n/documentTitle'
import { ensureAdminMessages } from '@/i18n/adminMessages'
import { captureConsentConfirmationFragment } from '@/utils/consent-confirmation'
import { captureMembershipConfirmationFragment } from '@/utils/membership-confirmation'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { titleKey: 'routes.home', requiresAuth: false }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { titleKey: 'routes.login', requiresAuth: false }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    meta: { titleKey: 'routes.profile', requiresAuth: true }
  },
  {
    path: '/account/data',
    name: 'account-data',
    component: () => import('../views/AccountDataView.vue'),
    meta: { titleKey: 'routes.accountData', requiresAuth: true }
  },
  {
    path: '/account/delete/confirm',
    name: 'account-delete-confirm',
    component: () => import('../views/AccountDeletionConfirmView.vue'),
    meta: { titleKey: 'routes.accountDeleteConfirm', requiresAuth: true }
  },
  {
    path: '/account/confirm-age',
    name: 'account-confirm-age',
    component: () => import('../views/AccountConfirmAgeView.vue'),
    meta: { titleKey: 'routes.accountConfirmAge', requiresAuth: true }
  },
  {
    path: '/account/closed',
    name: 'account-closed',
    component: () => import('../views/AccountClosedView.vue'),
    meta: { titleKey: 'routes.accountClosed', requiresAuth: false }
  },
  {
    path: '/account/not-invited',
    name: 'account-not-invited',
    component: () => import('../views/AccountNotInvitedView.vue'),
    meta: { titleKey: 'routes.accountNotInvited', requiresAuth: false }
  },
  {
    path: '/account/verify-email',
    name: 'account-verify-email',
    component: () => import('../views/AccountVerifyEmailView.vue'),
    meta: { titleKey: 'routes.accountVerifyEmail', requiresAuth: false }
  },
  {
    path: '/organizations/:orgId/confidentiality',
    name: 'admin-acknowledgement',
    component: () => import('../views/AdminAcknowledgementView.vue'),
    meta: { titleKey: 'routes.adminAcknowledgement', requiresAuth: true }
  },
  {
    path: '/rights',
    name: 'rights',
    component: () => import('../views/RightsView.vue'),
    meta: { titleKey: 'routes.rights', requiresAuth: true }
  },
  {
    path: '/rights/consents/:consentId/confirm',
    name: 'consent-confirm',
    component: () => import('../views/ConsentConfirmationView.vue'),
    meta: { titleKey: 'routes.consentConfirm', requiresAuth: true }
  },
  {
    path: '/membership/confirm',
    name: 'membership-confirm',
    component: () => import('../views/MembershipConfirmationView.vue'),
    meta: { titleKey: 'routes.membershipConfirm', requiresAuth: false }
  },
  {
    path: '/organizations',
    name: 'organizations',
    component: () => import('../views/OrganizationsView.vue'),
    meta: { titleKey: 'routes.organizations', requiresAuth: true }
  },
  {
    path: '/profiles/:kind',
    name: 'profile-editor',
    component: () => import('../views/ProfileEditorView.vue'),
    meta: { titleKey: 'routes.profileEditor', requiresAuth: true }
  },
  {
    path: '/callback',
    name: 'callback',
    component: () => import('../views/HomeView.vue'), // Temporary component, auth guard will redirect
    meta: { titleKey: 'routes.signingIn', requiresAuth: false, isCallback: true }
  },
  {
    path: '/admin',
    component: () => import('../components/AdminRouterView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    // KORA-139: the Admin Panel's catalog is a separate chunk, merged on first entry. The global
    // `beforeEach` guard runs first, so a non-admin never downloads it.
    beforeEnter: ensureAdminMessages,
    children: [
      {
        path: '',
        name: 'admin-home',
        component: () => import('@admin/views/HomeView.vue')
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@admin/views/UserManagementView.vue')
      },
      {
        path: 'organizations',
        name: 'admin-organizations',
        component: () => import('@admin/views/OrganizationManagementView.vue')
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: () => import('@admin/views/AuditLogView.vue')
      },
      {
        path: 'security',
        name: 'admin-security',
        component: () => import('@admin/views/SecurityDashboardView.vue')
      },
      {
        path: 'sessions',
        name: 'admin-sessions',
        component: () => import('@admin/views/SessionManagementView.vue')
      },
      {
        path: 'mfa',
        name: 'admin-mfa',
        component: () => import('@admin/views/MfaManagementView.vue')
      },
      {
        path: 'external-entitlements/sources',
        name: 'admin-external-entitlement-sources',
        component: () => import('@admin/views/ExternalEntitlementSourcesView.vue')
      },
      {
        path: 'external-entitlements/sources/:sourceId',
        name: 'admin-external-entitlement-source-detail',
        component: () => import('@admin/views/ExternalEntitlementSourceDetailView.vue')
      },
      {
        path: 'external-entitlements/sources/:sourceId/rules/:ruleId',
        name: 'admin-external-entitlement-rule-editor',
        component: () => import('@admin/views/ExternalEntitlementRuleEditorView.vue')
      },
      {
        path: 'mingle-mage/releases',
        name: 'admin-mingle-mage-releases',
        component: () => import('@admin/views/SequenceReleaseManagementView.vue')
      },
      {
        path: 'mingle-mage/releases/:releaseId',
        name: 'admin-mingle-mage-release-detail',
        component: () => import('@admin/views/SequenceReleaseDetailView.vue')
      },
      {
        path: 'mingle-mage/factory-sets',
        name: 'admin-mingle-mage-factory-sets',
        component: () => import('@admin/views/FactorySetsView.vue')
      },
      {
        path: 'mingle-mage/factory-unassigned',
        name: 'admin-mingle-mage-factory-unassigned',
        component: () => import('@admin/views/FactoryUnassignedView.vue')
      },
      {
        path: 'mingle-mage/factory-sets/:setId',
        name: 'admin-mingle-mage-factory-set-detail',
        component: () => import('@admin/views/FactorySetDetailView.vue')
      },
      {
        path: 'atlas/curation',
        name: 'admin-atlas-curation-queue',
        component: () => import('@admin/views/AtlasCurationQueueView.vue')
      },
      {
        path: 'atlas/curation/entries/:uuid',
        name: 'admin-atlas-curation-entry',
        component: () => import('@admin/views/AtlasCurationEntryView.vue')
      },
      {
        path: ':pathMatch(.*)*',
        name: 'admin-not-found',
        component: () => import('@admin/views/NotFoundView.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { titleKey: 'routes.notFound', requiresAuth: false }
  }
]

// KORA-114: hash links to an element on the page we are already on (the account menu's rights
// link is now the `/rights` route, KORA-115, but other in-app anchors still land here). A fresh page
// load is scrolled by the page itself; this covers in-app navigation onto a page that is already
// mounted, where nothing remounts.
//
// Only plain element ids are handled. Never a selector (a fragment like the membership
// confirmation's `#claim=...&token=...` is not one and would throw), and never anything that looks
// like key=value data: vue-router logs the hash it cannot resolve, which would put a token in the
// console.
/** The position vue-router saved for back/forward navigation. */
type SavedScrollPosition = { left: number; top: number; behavior?: ScrollBehavior }

export function scrollToHash(
  to: { hash: string; path?: string },
  _from?: unknown,
  savedPosition?: SavedScrollPosition | null
): false | { el: HTMLElement } | SavedScrollPosition {
  // Back/forward and reload: vue-router sets history.scrollRestoration to 'manual' once a
  // scrollBehavior exists, so restoring the saved position is now our job.
  if (savedPosition) return savedPosition
  if (to.path === '/membership/confirm' || to.hash.length < 2 || to.hash.includes('=')) return false
  let id: string
  try {
    id = decodeURIComponent(to.hash.slice(1))
  } catch {
    return false
  }
  const el = document.getElementById(id)
  return el === null ? false : { el }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: scrollToHash
})

// Custom auth guard using the auth store
router.beforeEach(async (to, _from, next) => {
  const consentConfirmation = to.name === 'consent-confirm'
  if (consentConfirmation && to.hash) {
    captureConsentConfirmationFragment(to.path, to.hash)
    next({ path: to.path, query: to.query, hash: '', replace: true })
    return
  }
  const returnPath = consentConfirmation ? to.path : to.fullPath
  if (to.path === '/membership/confirm') captureMembershipConfirmationFragment()
  // (D7, KORA-66) useAuthStore() itself does not throw today — getDefaultAuth0Config()
  // failures are already caught inside the store — but this is belt-and-braces defense
  // in depth: if a future change reintroduces a throw here, the guard must still resolve
  // navigation instead of leaving it hanging (a blank page either way).
  let authStore: ReturnType<typeof useAuthStore>
  try {
    authStore = useAuthStore()
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Auth store construction failure
      console.error('Auth store initialization error:', err)
    }
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true)
    if (requiresAuth) {
      next({ path: '/login', query: { error: 'auth_unavailable', redirect: returnPath } })
    } else {
      next()
    }
    return
  }

  // Handle Auth0 redirect callback
  if (to.path === '/callback' && to.query.code && to.query.state) {
    try {
      // Use the auth store's handleRedirectCallback to maintain state
      const targetUrl = await authStore.handleRedirectCallback()
      next(safeRedirectPath(targetUrl, '/'))
      return
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Auth callback debugging
        console.error('Auth callback error:', err)
      }
      next('/login?error=callback_failed')
      return
    }
  }

  // Await the auth store's initialization promise so navigation is
  // deterministic — no busy-wait polling on isLoading. initialize() is
  // documented to never reject; the try/catch is defense in depth against a
  // future lib change re-introducing rejection into this and two other
  // unguarded app call sites (KORA-66).
  try {
    await authStore.initialize()
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- initialize() must never reject; log if it does
      console.error('Auth initialize error:', err)
    }
  }

  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL. When auth could not be determined
    // (bounded init timed out, or the config threw), flag it so LoginView
    // does not auto-loop back into Auth0 (KORA-66).
    next({
      path: '/login',
      query: {
        redirect: returnPath,
        ...(authStore.authUnavailable ? { error: 'auth_unavailable' } : {})
      }
    })
    return
  }

  // KORA-154: a signed-in member confirms they are 18 or older before using anything else.
  if (requiresAuth && to.name !== 'account-confirm-age' && to.name !== 'callback') {
    if ((await useAgeGateStore().check()) === 'missing') {
      next({ name: 'account-confirm-age', query: { redirect: returnPath }, replace: true })
      return
    }
  }

  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin === true)
  // Admin is the database trust level, which only the loaded Kora profile carries (KORA-165).
  if (requiresAdmin) await authStore.waitForKoraUser()
  if (requiresAdmin && !authStore.isAdmin) {
    next({ path: '/' })
    return
  }

  // KORA-114: remember a validated "Back to <product>" target from the shared account menu.
  if (to.query.return !== undefined) {
    useAccountReturnStore().capture(
      Array.isArray(to.query.return) ? to.query.return[0] : to.query.return
    )
  }

  next()
})

// ADR-010 D5: the page title follows the route's `meta.titleKey`. A language change re-applies it
// (`useLocaleSync`).
router.afterEach(to => applyDocumentTitle(to))

export default router
